import { Component, OnInit, ComponentFactoryResolver, ChangeDetectorRef, ElementRef } from '@angular/core';
import { StepComponent } from '../step.component';
import { UntypedFormControl } from '@angular/forms';
import { FormGroupDirective } from '@angular/forms';
import { FruHttpService } from '../../../../../../services/fru-http.service';
import { JornayaService } from '../../../../../../services/jornaya.service';
import { AnalyticsService } from '../../../../../../services/analytics.service';
import { ButtonService } from '../../../../../../services/buttons.service';
import { Lead } from '../../../../../../shared/lead';
import { CommunicationService } from '../../../../../../services/communication.service';
import { RandomService } from '../../../../../../services/random.service';
import { BrandService } from '../../../../../../services/brand.service';
import { SvgFixService } from '../../../../../../services/svg-fix.service';
import { FastModeService } from '../../../../../../services/fast-mode.service';

@Component({
    selector: 'app-button-step',
    templateUrl: './button-step.component.html',
    styleUrls: ['./button-step.component.scss'],
    standalone: false
})

export class ButtonStepComponent extends StepComponent implements OnInit {
  control: UntypedFormControl;
  noBrandTitle: string;
  title: string;
  subtext?: string;
  hook?: string;
  default?: string;
  touchHandled = false;
  onlyClick: boolean;
  under500Title: string = "Is your credit score over 500?";

  buttonList: {
    text: string,
    value: string,
    imageName?: string,
    img?: string,   // a full image path instead of buttonPath + 'new-' + imageName + fileType
    className?: string,
    borderStyle?: string,
    default?: boolean,
  }[];
  buttonPath: string;
  fileType: string;

  poorCreditScore: boolean = false;
  poorCredScoreBtns: {
    text: string,
    value: string,
    imageName?: string,
    default?: boolean
  }[] = [
      { text: 'Yes', value: 'Low_Fair', imageName: 'yes', default: true },
      { text: 'No', value: 'Poor', imageName: 'no-x' },
    ];

  constructor(
    parent: FormGroupDirective,
    componentFactoryResolver: ComponentFactoryResolver,
    cdRef: ChangeDetectorRef,
    elementRef: ElementRef,
    fruHttpService: FruHttpService,
    jornayaService: JornayaService,
    analyticsService: AnalyticsService,
    communication: CommunicationService,
    buttonService: ButtonService,
    randomService: RandomService,
    brandService: BrandService,
    svgFixService: SvgFixService,
    public fastMode: FastModeService
  ) {
    super(
      parent,
      componentFactoryResolver,
      cdRef,
      elementRef,
      fruHttpService,
      jornayaService,
      analyticsService,
      communication,
      randomService,
      brandService,
      svgFixService
    );
    this.isMobileSafari();
    //This will be replaced with a call to the button service once it exists
    const buttonSet = buttonService.get();
    const subdirectory = buttonSet.buttonPath;
    this.buttonPath = `/assets/images/button-sets/${subdirectory}/`;
    this.fileType = buttonSet.fileType;
  }

  ngOnInit() { }

  /** Guard so FAST only auto-advances a given step instance once. */
  private fastAdvanced = false;

  /**
   * FAST mode (FRU-21170): when enabled, this step auto-selects the option the
   * form already marks as its default and taps through after a brief pause
   * (keeps a visible human-like cadence). Steps with no default option don't
   * auto-advance — FAST gracefully stops and waits for the user.
   */
  fastTry(): void {
    if (!this.fastMode || !this.fastMode.canAutoAdvance || this.fastAdvanced) {
      return;
    }
    // The step's own initStep may have already answered (e.g. maximumEquity).
    if (this.control && this.control.value !== '' && this.control.value != null) {
      return;
    }
    // Prefer a FAST-inferred answer (keyword rule, etc.) for this step's control,
    // otherwise fall back to the option the form marks as its built-in default.
    const name = this.fastControlName();
    const override = name ? this.fastMode.getAnswer(name) : null;
    let target = (override != null)
      ? (this.buttonList || []).find(b => b.value === override)
      : null;
    if (!target) {
      // maximumEquity ("max home equity?") is the 1st cash-out question: only the
      // equity loan types get a FAST override here; refinance/purchase stop for the user.
      if (name === 'maximumEquity') {
        return;
      }
      target = (this.buttonList || []).find(b => b.default);
    }
    if (!target) {
      return; // no default option -> FAST gracefully stops here for the user
    }
    this.fastAdvanced = true;
    setTimeout(() => {
      // Re-check: the user may have hit Stop during the pause.
      if (this.fastMode.enabled) {
        this.fastMode.markResolved(this.title || this.noBrandTitle);
        this.submit(target);
      }
    }, 650);
  }

  /** Reverse-lookup this step's form-control name (e.g. 'propertyType'). */
  private fastControlName(): string | null {
    if (!this.control || !this.form || !this.form.controls) {
      return null;
    }
    return Object.keys(this.form.controls).find(k => this.form.controls[k] === this.control) || null;
  }

  isMobileSafari(): void {
    const userAgent = navigator.userAgent || navigator.vendor || window['opera'];
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/CriOS|Chrome/.test(userAgent);
    const isMobileSafari = isIOS && isSafari;
    this.onlyClick = !isMobileSafari;
  }

  submit(button, event?: Event): void {
    // safari bug fix, check if device is using touch action
    if (event && this.isTouchHandled(event)) {
      return;
    }
    // If there's no value, this was probably an enter key press.
    // Click the default button if available.
    if (typeof button === 'undefined') {
      const defaultButton = this.elementRef.nativeElement.querySelector('[default]');
      if (defaultButton) {
        defaultButton.click();
        return;
      }
      // not possible case if there is no default button
      return;
    }

    const value = button.value;
    const text = button.text;

    for (let butt of this.buttonList) {
      if (butt.value == value && butt.text == text) {
        butt.default = true;
        butt.borderStyle = '2px solid rgb(118, 188, 146)';
      } else {
        butt.borderStyle = null;
        butt.default = false;
      }
    }

    this.analyticsService.trackQuestionAnswered();

    if (this.shouldRedirectToOffers(value)) {
      this.poorCreditScore = true;
      this.svgFixService.fixSvgUse();
      return;
    }

    if (this.shouldRedirectToPLOffers(value)) {
      const lead: Lead = {
        cashOut: this.form.value.preCashOut,
        desiredLoanAmount: this.form.value.desiredLoanAmount,
        creditRating: this.form.value.creditRating,
      };

      return this.fruHttpService.redirectOffers(lead);;
    }

    this.control.setValue(value);
    this.nextStep();
  }

  validateCreditScoreOver500(value: string, event?: Event): void {
    // safari bug fix, check if device is using touch action
    if (event && this.isTouchHandled(event)) {
      return;
    }
    // if (value === 'Poor') {
    //   if (this.form.value.cashOut >= 1000
    //     || (this.form.controls.maximumEquity
    //       && this.form.controls.maximumEquity.value == 'yes')
    //   ) {
    //     let cashOut = this.form.value.cashOut >= 1000
    //       ? this.form.value.cashOut
    //       : this.form.value.propertyValue * 0.9 - this.form.value.firstMortgageBalance;
    //     let isOwn = this.form.value.housingSituation == 'Own'
    //       ? '1'
    //       : '0';

    //     const lead: Lead = {
    //       type: this.form.value.type,
    //       cashOut: cashOut,
    //       desiredLoanAmount: this.form.value.desiredLoanAmount,
    //       propertyType: this.form.value.propertyType,
    //       creditRating: value,
    //       housingSituation: isOwn,
    //     };

    //     this.fruHttpService.redirectOffers(lead);
    //   } else {
    //     this.fruHttpService.redirectOffers(undefined);
    //   }
    // } else {
    this.control.setValue(value);
    this.poorCreditScore = false;
    this.nextStep();
    // }

    return;
  }

  private isTouchHandled(event): boolean {
    if (event) {
      this.touchHandled = true;
      event.preventDefault(); // Prevents the default touch action (e.g., scrolling).
      return false;
    } else if (!event && this.touchHandled) {
      return true;
    }
  }
}
